import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneIcon from "@mui/icons-material/Phone";
import SettingsIcon from "@mui/icons-material/Settings";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AddIcon from "@mui/icons-material/Add";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StorefrontIcon from "@mui/icons-material/Storefront";

// Components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import RemoveModal from "../../modals/removeModal";
import CreateWhatsAppInstanceModal from "./CreateWhatsAppInstanceModal";
import ConnectWhatsAppModal from "./ConnectWhatsAppModal";
import WhatsAppNotificationConfigModal from "./WhatsAppNotificationConfigModal";

// Services & Utils
import {
  listWhatsAppInstances,
  deleteWhatsAppInstance,
  disconnectWhatsApp,
  getWhatsAppStatus,
} from "../../../api/DeliveryCenter/whatsapp";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

const WHATSAPP_GREEN = "#25D366";
const WHATSAPP_GREEN_DARK = "#1da851";

const STATUS_CONFIG = {
  connected: {
    label: "Conectado",
    color: WHATSAPP_GREEN,
    bgColor: alpha(WHATSAPP_GREEN, 0.1),
    chipColor: "success",
  },
  connecting: {
    label: "Conectando",
    color: "#f59e0b",
    bgColor: alpha("#f59e0b", 0.1),
    chipColor: "warning",
  },
  created: {
    label: "Aguardando conexão",
    color: "#3b82f6",
    bgColor: alpha("#3b82f6", 0.1),
    chipColor: "info",
  },
  disconnected: {
    label: "Desconectado",
    color: "#94a3b8",
    bgColor: alpha("#94a3b8", 0.1),
    chipColor: "default",
  },
};

function InstanceCard({
  instance,
  onDelete,
  onDisconnect,
  onRefreshStatus,
  onSettings,
  onConnect,
}) {
  const theme = useTheme();
  const [refreshing, setRefreshing] = useState(false);
  const statusConfig = STATUS_CONFIG[instance.status] || STATUS_CONFIG.disconnected;
  const isConnected = instance.status === "connected";

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefreshStatus(instance);
    setRefreshing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
    >
      {/* Status accent bar */}
      <Box
        sx={{
          height: 4,
          borderRadius: "4px 4px 0 0",
          background: isConnected
            ? `linear-gradient(90deg, ${WHATSAPP_GREEN}, ${alpha(WHATSAPP_GREEN, 0.4)})`
            : `linear-gradient(90deg, ${statusConfig.color}, ${alpha(statusConfig.color, 0.3)})`,
        }}
      />

      {/* Main card body */}
      <Box
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          borderTop: "none",
          borderRadius: "0 0 12px 12px",
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
        }}
      >
        {/* Identity section */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: isConnected
                  ? alpha(WHATSAPP_GREEN, 0.12)
                  : alpha(theme.palette.grey[400], 0.15),
                color: isConnected ? WHATSAPP_GREEN : theme.palette.grey[500],
                boxShadow: isConnected
                  ? `0 0 0 3px ${alpha(WHATSAPP_GREEN, 0.15)}`
                  : "none",
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 30 }} />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
                  {instance.instance_name}
                </Typography>
                <Chip
                  icon={
                    <FiberManualRecordIcon
                      sx={{ fontSize: "10px !important", color: `${statusConfig.color} !important` }}
                    />
                  }
                  label={statusConfig.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    backgroundColor: statusConfig.bgColor,
                    color: statusConfig.color,
                    border: `1px solid ${alpha(statusConfig.color, 0.25)}`,
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />
              </Stack>

              {instance.phone_number ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {instance.phone_number}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                  Nenhum número vinculado
                </Typography>
              )}

              {/* Linked restaurants */}
              {instance.restaurants && instance.restaurants.length > 0 ? (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <StorefrontIcon sx={{ fontSize: 14, color: "text.secondary", mt: 0.3 }} />
                  {instance.restaurants.map((r) => (
                    <Chip
                      key={r.id}
                      label={r.name}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  ))}
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                  <StorefrontIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                  <Typography variant="caption" color="text.disabled">
                    Todos os restaurantes
                  </Typography>
                </Stack>
              )}
            </Box>

            {/* Refresh button — subtle, top-right */}
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.92 }}>
              <Box
                component="button"
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  borderRadius: "8px",
                  backgroundColor: "transparent",
                  cursor: refreshing ? "wait" : "pointer",
                  color: theme.palette.text.secondary,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.primary.main,
                  },
                  "&:disabled": { opacity: 0.4 },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 16,
                    animation: refreshing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </Box>
            </motion.div>
          </Stack>
        </Box>

        <Divider sx={{ mx: 3 }} />

        {/* Actions section */}
        <Box sx={{ p: 2.5, pt: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
          >
            {!isConnected && (
              <Button
                variant="contained"
                startIcon={<QrCode2Icon />}
                onClick={() => onConnect(instance)}
                size="small"
                sx={{
                  backgroundColor: WHATSAPP_GREEN,
                  "&:hover": { backgroundColor: WHATSAPP_GREEN_DARK },
                  borderRadius: "8px",
                  fontWeight: 500,
                  px: 2,
                }}
              >
                Conectar via QR Code
              </Button>
            )}

            {isConnected && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => onSettings(instance)}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 500,
                    px: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    color: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  Configurações
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<LinkOffIcon />}
                  onClick={() => onDisconnect(instance)}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 500,
                    px: 2,
                    borderColor: alpha(theme.palette.warning.main, 0.4),
                    color: theme.palette.warning.dark,
                    "&:hover": {
                      borderColor: theme.palette.warning.main,
                      backgroundColor: alpha(theme.palette.warning.main, 0.05),
                    },
                  }}
                >
                  Desconectar
                </Button>
              </>
            )}

            <Box sx={{ flex: 1 }} />

            <Button
              variant="text"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(instance)}
              size="small"
              sx={{
                borderRadius: "8px",
                fontWeight: 500,
                px: 2,
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.06),
                },
              }}
            >
              Remover
            </Button>
          </Stack>
        </Box>
      </Box>
    </motion.div>
  );
}

function EmptyState({ onConnect }) {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          px: 3,
          border: `2px dashed ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: "12px",
          backgroundColor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: alpha(WHATSAPP_GREEN, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 38, color: WHATSAPP_GREEN }} />
        </Box>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          Nenhum WhatsApp conectado
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mb: 3.5, maxWidth: 360 }}
        >
          Conecte seu WhatsApp para enviar notificações automáticas e se
          comunicar com seus clientes diretamente pelo número do negócio.
        </Typography>

        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onConnect}
            sx={{
              backgroundColor: WHATSAPP_GREEN,
              "&:hover": { backgroundColor: WHATSAPP_GREEN_DARK },
              borderRadius: "10px",
              fontWeight: 500,
              px: 3,
              py: 1,
            }}
          >
            Conectar WhatsApp
          </Button>
        </motion.div>
      </Box>
    </motion.div>
  );
}

export default function WhatsAppInstanceModal({ show, closeModal }) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const [instances, setInstances] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [configTarget, setConfigTarget] = useState(null);
  const [connectInstance, setConnectInstance] = useState(null);

  useEffect(() => {
    if (show) {
      loadInstances();
    }
  }, [show]);

  const loadInstances = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await listWhatsAppInstances();
      setInstances(data);
    } catch (e) {
      const message =
        e.response?.data?.error || "Erro ao carregar instâncias WhatsApp.";
      setErrorMessage(message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreateModal = useCallback(() => {
    setShowCreateModal((prev) => !prev);
  }, []);

  const handleInstanceCreated = useCallback(() => {
    setShowCreateModal(false);
    loadInstances();
  }, [loadInstances]);

  const handleDeleteModalOpen = useCallback((inst) => {
    setDeleteTarget(inst);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleDeleteInstance = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteWhatsAppInstance(deleteTarget.id);
      handleDeleteModalClose();
      setInstances((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      dispatchSnackBarMessage(
        `whatsapp-deleted-${new Date()}`,
        "Instância WhatsApp removida com sucesso",
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        `whatsapp-error-${new Date()}`,
        "Erro ao remover instância WhatsApp",
        "error"
      );
    }
  }, [deleteTarget, handleDeleteModalClose]);

  const handleDisconnect = useCallback(async (inst) => {
    try {
      await disconnectWhatsApp(inst.id);
      await loadInstances();
      dispatchSnackBarMessage(
        `whatsapp-disconnected-${new Date()}`,
        "WhatsApp desconectado",
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        `whatsapp-error-${new Date()}`,
        "Erro ao desconectar",
        "error"
      );
    }
  }, [loadInstances]);

  const handleRefreshStatus = useCallback(async (inst) => {
    try {
      await getWhatsAppStatus(inst.id);
      await loadInstances();
    } catch (e) {
      dispatchSnackBarMessage(
        `whatsapp-error-${new Date()}`,
        "Erro ao atualizar status",
        "error"
      );
    }
  }, [loadInstances]);

  const handleOpenNotificationConfig = useCallback((inst) => {
    setConfigTarget(inst);
  }, []);

  const handleCloseNotificationConfig = useCallback(() => {
    setConfigTarget(null);
    loadInstances();
  }, [loadInstances]);

  const handleConnect = useCallback((inst) => {
    setConnectInstance(inst);
  }, []);

  const handleConnectClose = useCallback(() => {
    setConnectInstance(null);
  }, []);

  const handleConnected = useCallback(() => {
    setConnectInstance(null);
    loadInstances();
  }, [loadInstances]);

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="90%"
      maxWidth="560px"
    >
      <ModalHeader
        title="WhatsApp"
        icon={WhatsAppIcon}
        onClose={closeModal}
      />
      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {typeof errorMessage === "string"
                    ? errorMessage
                    : "Erro ao carregar instâncias"}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box>
              <Skeleton variant="rectangular" height={4} sx={{ borderRadius: "4px 4px 0 0", mb: 0 }} />
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: "0 0 12px 12px" }} />
            </Box>
          ) : (
            <AnimatePresence mode="wait">
              {instances.length > 0 ? (
                <Stack spacing={2}>
                  {instances.map((inst) => (
                    <InstanceCard
                      key={inst.id}
                      instance={inst}
                      onDelete={() => handleDeleteModalOpen(inst)}
                      onDisconnect={() => handleDisconnect(inst)}
                      onRefreshStatus={handleRefreshStatus}
                      onSettings={() => handleOpenNotificationConfig(inst)}
                      onConnect={handleConnect}
                    />
                  ))}
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleCreateModal}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 500,
                        px: 3,
                        borderColor: alpha(WHATSAPP_GREEN, 0.4),
                        color: WHATSAPP_GREEN,
                        "&:hover": {
                          borderColor: WHATSAPP_GREEN,
                          backgroundColor: alpha(WHATSAPP_GREEN, 0.05),
                        },
                      }}
                    >
                      Adicionar Instância
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <EmptyState key="empty" onConnect={handleCreateModal} />
              )}
            </AnimatePresence>
          )}
        </Box>

        <RemoveModal
          title="Remover WhatsApp"
          message={`Tem certeza que deseja remover a instância "${
            deleteTarget?.instance_name ?? ""
          }"? Esta ação não pode ser desfeita.`}
          show={!!deleteTarget}
          toggleModal={handleDeleteModalClose}
          onRemove={handleDeleteInstance}
        />

        {showCreateModal && (
          <CreateWhatsAppInstanceModal
            show={showCreateModal}
            closeModal={handleCreateModal}
            onCreated={handleInstanceCreated}
          />
        )}

        {connectInstance && (
          <ConnectWhatsAppModal
            show={!!connectInstance}
            closeModal={handleConnectClose}
            instance={connectInstance}
            onConnected={handleConnected}
          />
        )}

        {configTarget && (
          <WhatsAppNotificationConfigModal
            show={!!configTarget}
            closeModal={handleCloseNotificationConfig}
            instanceId={configTarget.id}
          />
        )}
      </ModalContent>
    </AnimatedModal>
  );
}
